export default function SafetyHarborProcessingTimeSection() {
  return (
    <section id="processing-options" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Speed Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Processing Times for Safety Harbor Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Choose the timeline that fits your travel schedule. We process every application through our registered courier channels — no public passport agency appointment required for you.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {/* Emergency */}
          <div className="bg-[#1A365D] rounded-lg p-6 border-2 border-[#F6C344] flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]">
                <i className="ri-flashlight-line text-[#1A365D] text-sm"></i>
              </div>
              <span className="bg-[#F6C344] text-[#1A365D] text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">Most Urgent</span>
            </div>
            <p className="font-serif text-3xl font-bold text-white mb-1">24 Hours</p>
            <p className="text-white/60 text-xs mb-5">Emergency Same-Day Processing</p>
            <p className="text-white/70 text-sm leading-relaxed mb-5 flex-1">
              For documented life-or-death emergencies and travelers departing within 24–72 hours. Available with 24/7 human support.
            </p>
            <div className="space-y-2 mb-5">
              {["Real human support 24/7", "Same-day agency submission", "Secure overnight return shipping", "Best for TPA departures <72hrs"].map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-white/80 text-xs">
                  <i className="ri-check-line text-[#F6C344]"></i>
                  <span>{f}</span>
                </div>
              ))}
            </div>
            <a
              href="https://fastpassportcenter.com/checkout/"
              className="block text-center bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm py-3 rounded-md transition-all whitespace-nowrap"
            >
              Get Emergency Passport
            </a>
          </div>

          {/* Expedited Popular */}
          <div className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all duration-200 flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#EEF2F8]">
                <i className="ri-time-line text-[#1A365D] text-sm"></i>
              </div>
              <span className="bg-[#1A365D] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">Most Popular</span>
            </div>
            <p className="font-serif text-3xl font-bold text-[#1A365D] mb-1">2–9 Days</p>
            <p className="text-gray-400 text-xs mb-5">Expedited Processing</p>
            <p className="text-gray-600 text-sm leading-relaxed mb-5 flex-1">
              The right choice for most Safety Harbor travelers with upcoming Tampa Bay cruises, flights from TPA, or family trips planned 2–3 weeks out.
            </p>
            <div className="space-y-2 mb-5">
              {["Full document review included", "Agency submission within 48hrs", "Tracking updates via email", "Best for planned travel 2–4 weeks away"].map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-gray-500 text-xs">
                  <i className="ri-check-line text-[#F6C344]"></i>
                  <span>{f}</span>
                </div>
              ))}
            </div>
            <a
              href="https://fastpassportcenter.com/checkout/"
              className="block text-center bg-[#1A365D] hover:bg-[#152c4d] text-white font-bold text-sm py-3 rounded-md transition-all whitespace-nowrap"
            >
              Start Expedited Service
            </a>
          </div>

          {/* Standard */}
          <div className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all duration-200 flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#EEF2F8]">
                <i className="ri-calendar-line text-[#1A365D] text-sm"></i>
              </div>
            </div>
            <p className="font-serif text-3xl font-bold text-[#1A365D] mb-1">3–5 Weeks</p>
            <p className="text-gray-400 text-xs mb-5">Standard Expedited</p>
            <p className="text-gray-600 text-sm leading-relaxed mb-5 flex-1">
              Faster than routine government processing with full support. Ideal for Safety Harbor travelers planning ahead for spring break, summer, or holiday trips.
            </p>
            <div className="space-y-2 mb-5">
              {["Document checklist & review", "Courier channel submission", "Email status updates", "Best for travel 6+ weeks away"].map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-gray-500 text-xs">
                  <i className="ri-check-line text-[#F6C344]"></i>
                  <span>{f}</span>
                </div>
              ))}
            </div>
            <a
              href="https://fastpassportcenter.com/checkout/"
              className="block text-center border-2 border-[#1A365D] hover:bg-[#1A365D] hover:text-white text-[#1A365D] font-bold text-sm py-3 rounded-md transition-all whitespace-nowrap"
            >
              Choose Standard Speed
            </a>
          </div>

          {/* Self-Service */}
          <div className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all duration-200 flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#EEF2F8]">
                <i className="ri-user-line text-[#1A365D] text-sm"></i>
              </div>
            </div>
            <p className="font-serif text-3xl font-bold text-[#1A365D] mb-1">1–3 Months</p>
            <p className="text-gray-400 text-xs mb-5">DIY + Support Package</p>
            <p className="text-gray-600 text-sm leading-relaxed mb-5 flex-1">
              Complete guidance, form review, and shipping instructions for travelers who want expert support without the expedited fee. Ship from any Safety Harbor, Clearwater, or Dunedin location.
            </p>
            <div className="space-y-2 mb-5">
              {["Expert form review", "Shipping instructions included", "Application tracking guide", "Best for non-urgent renewals"].map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-gray-500 text-xs">
                  <i className="ri-check-line text-[#F6C344]"></i>
                  <span>{f}</span>
                </div>
              ))}
            </div>
            <a
              href="https://fastpassportcenter.com/checkout/"
              className="block text-center border-2 border-gray-300 hover:border-[#1A365D] text-gray-600 hover:text-[#1A365D] font-bold text-sm py-3 rounded-md transition-all whitespace-nowrap"
            >
              Get DIY Support
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}